import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import styles from '../assets/keepMeInformed.module.css';
import DataTable from '../components/DataTable';
import keepMeInformedService from '../services/keepMeInformedService';
import guardianDashboardService from '../services/guardianDashboardService';

const KeepMeInformed = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [guardianId, setGuardianId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGuardian, setLoadingGuardian] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [searchValue, setSearchValue] = useState('');

  // Breadcrumbs following Laravel implementation
  const breadcrumbs = [
    { name: 'Dashboard', path: '/guardian-dashboard' },
    { name: 'Available Spaces', path: '/spaces/available' },
    { name: 'Keep me informed', path: '/keep-me-informed' },
  ];

  // Load guardian ID first (following GuardianEditProfile pattern)
  useEffect(() => {
    const loadGuardianId = async () => {
      try {
        setLoadingGuardian(true);
        console.log('Loading guardian ID for KeepMeInformed...');

        // Get the guardian ID from the dashboard data
        const dashboardData = await guardianDashboardService.getDashboardData();
        console.log('Dashboard data:', dashboardData);

        if (dashboardData?.guardian?.id) {
          console.log('Setting guardian ID:', dashboardData.guardian.id);
          setGuardianId(dashboardData.guardian.id);
        } else {
          console.error(
            'Guardian ID not found in dashboard data:',
            dashboardData
          );
          toast.error(
            'Guardian ID not found. Please refresh the page or contact support.'
          );
          navigate('/guardian-dashboard');
        }
      } catch (error) {
        console.error('Error loading guardian ID:', error);
        toast.error('Failed to load guardian data');
        navigate('/guardian-dashboard');
      } finally {
        setLoadingGuardian(false);
      }
    };

    if (user) {
      loadGuardianId();
    }
  }, [user, navigate]);

  // Fetch on search/pagination changes (Laravel DataTable behavior)
  useEffect(() => {
    if (user && guardianId) {
      fetchNotifications();
    }
  }, [user, guardianId, searchValue, pagination.currentPage]);

  const fetchNotifications = async () => {
    try {
      if (!guardianId) {
        console.log('Guardian ID not available yet, skipping fetch');
        return;
      }

      setLoading(true);
      console.log('Fetching notifications for guardian ID:', guardianId);

      const start = (pagination.currentPage - 1) * pagination.itemsPerPage;

      // Laravel DataTable format - exact same as Laravel PropertyViewingsController@getPropertyViewingInfoOfLoggedUser
      const data = await keepMeInformedService.getNotifications({
        draw: 1,
        start,
        length: pagination.itemsPerPage,
        search: { value: searchValue },
        guardian_id: guardianId, // Include guardian ID in the request
      });

      // Laravel DataTable response format - exact same structure
      if (data && data.data) {
        setNotifications(data.data);
        const total = data.recordsTotal || 0;
        setPagination((prev) => ({
          ...prev,
          totalItems: total,
          totalPages: Math.ceil(total / prev.itemsPerPage) || 1,
          hasNextPage: prev.currentPage * prev.itemsPerPage < total,
          hasPrevPage: prev.currentPage > 1,
        }));
      } else {
        // Fallback for non-DataTable response (should not happen with Laravel)
        console.warn('Unexpected response format from Laravel API');
        setNotifications(data || []);
        setPagination((prev) => ({
          ...prev,
          totalItems: data?.length || 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        }));
      }
    } catch (error) {
      console.error('❌ [KeepMeInformed] Error fetching notifications:', error);
      console.error('❌ [KeepMeInformed] Error response:', error.response);
      console.error(
        '❌ [KeepMeInformed] Error status:',
        error.response?.status
      );

      // Handle Laravel-specific error cases exactly
      if (error.response?.status === 401) {
        console.log(
          '🔒 [KeepMeInformed] 401 Unauthorized - user needs to login'
        );
        // Don't redirect on 401, just show error
        toast.error('Please log in to view your notifications.');
      } else if (error.response?.status === 403) {
        console.log('🚫 [KeepMeInformed] 403 Forbidden - access denied');
        toast.error(
          'Access denied. You must be a guardian to view notifications.'
        );
      } else if (error.response?.status === 500) {
        console.log('💥 [KeepMeInformed] 500 Server Error');
        toast.error('Server error. Please try again later.');
      } else if (error.response?.status === 404) {
        console.log(
          '🔍 [KeepMeInformed] 404 Not Found - API endpoint not available'
        );
        toast.error(
          'Keep me informed service is not available yet. Please try again later.'
        );
      } else {
        console.log('❓ [KeepMeInformed] Unknown error:', error.message);
        toast.error('Failed to load notifications. Please try again.');
      }

      // Set empty state on error but don't logout
      setNotifications([]);
      setPagination((prev) => ({
        ...prev,
        totalItems: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNotification = async (notificationId) => {
    if (!guardianId) {
      toast.error('Guardian ID not available. Please refresh the page.');
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this notification?'))
      return;

    try {
      await keepMeInformedService.cancelNotification(
        notificationId,
        guardianId
      );
      toast.success('Your notification has been deleted!'); // Laravel exact message
      // Refresh the list
      fetchNotifications();
    } catch (error) {
      console.error('Error cancelling notification:', error);

      // Handle Laravel-specific error cases exactly as in PropertyViewingsController@deleteInfo
      if (error.response?.status === 401) {
        toast.error('Please log in to cancel notifications.');
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to perform this action!"); // Laravel exact message
      } else if (error.response?.status === 404) {
        toast.error('There is no notification to delete!'); // Laravel exact message
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error('Failed to cancel notification. Please try again.');
      }
    }
  };

  const handlePropertyClick = (propertyId) => {
    if (propertyId) {
      console.log(`✅ [KeepMeInformed] Navigating to property details: /spaces/show/${propertyId}`);
      navigate(`/spaces/show/${propertyId}`);
    } else {
      toast.warning('Property details not available');
    }
  };

  const handleRequestViewing = (propertyId) => {
    if (propertyId) {
      console.log(`✅ [KeepMeInformed] Navigating to property for viewing request: /spaces/show/${propertyId}`);
      navigate(`/spaces/show/${propertyId}`);
    } else {
      toast.warning('Property details not available');
    }
  };

  // Define columns following Laravel implementation exactly
  // Laravel DataTable columns: name, postcode, statusName, actions
  const columns = [
    {
      Header: 'Property',
      accessor: 'name',
      Cell: ({ value, row }) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handlePropertyClick(row.original.property_id);
          }}
          style={{
            fontWeight: '700',
            color: '#337ab7',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
          title="Click to view property details"
        >
          {/* Laravel returns HTML with <span style="font-weight: 700;">Property Name</span> */}
          {value || 'Property Name Not Available'}
        </a>
      ),
    },
    {
      Header: 'Postcode',
      accessor: 'postcode',
      Cell: ({ value }) => value || 'N/A',
    },
    {
      Header: 'Status',
      accessor: 'statusName',
      Cell: ({ value }) => {
        // Laravel logic: if property.vacantRoomsCount == 0, return 'Property Not Available'
        // Otherwise return statusName (e.g., 'Waiting for Viewing', 'New Viewing Date')
        return value || 'Unknown Status';
      },
    },
    {
      Header: 'Actions',
      accessor: 'id',
      Cell: ({ value, row }) => (
        <div className={styles.actionButtons}>
          {/* Laravel logic: if isAwaiting(), show Cancel button, otherwise show Request Viewing */}
          {row.original.isAwaiting ? (
            <button
              className={`${styles.actionButton} ${styles.cancelButton}`}
              onClick={() => handleCancelNotification(value)}
              title="Cancel notification for this property"
            >
              Cancel
            </button>
          ) : (
            <button
              className={`${styles.actionButton} ${styles.requestButton}`}
              onClick={() => handleRequestViewing(row.original.property_id)}
              title="Request viewing for this property"
            >
              Request Viewing
            </button>
          )}
        </div>
      ),
    },
  ];

  if (loadingGuardian || loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          {loadingGuardian
            ? 'Loading guardian data...'
            : 'Loading keep me informed notifications...'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Debug Toggle */}
      <div className={styles.debugToggle}>
        <label>
          <input type="checkbox" checked={false} onChange={() => {}} disabled />
          API Mode (Production)
        </label>
      </div>

      {/* Breadcrumbs - Following Laravel implementation exactly */}
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((crumb, index) => (
          <span key={index}>
            {index > 0 && <span className={styles.breadcrumbSeparator}>/</span>}
            {index === breadcrumbs.length - 1 ? (
              <span className={styles.breadcrumbItem}>{crumb.name}</span>
            ) : (
              <span
                className={styles.breadcrumbItem}
                onClick={() => navigate(crumb.path)}
                style={{ cursor: 'pointer', color: '#337ab7' }}
              >
                {crumb.name}
              </span>
            )}
          </span>
        ))}
      </div>

      {/* Header Section - Following Laravel implementation exactly */}
      <div className={styles.headerSection}>
        <div className={styles.panel}>
          <div className={styles.panelHeading}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h3>Keep me informed</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                {notifications.length === 0 && !loading && guardianId && (
                  <button
                    onClick={fetchNotifications}
                    className={styles.loadButton}
                    title="Load notifications"
                  >
                    Load Notifications
                  </button>
                )}
                <button
                  onClick={fetchNotifications}
                  className={styles.refreshButton}
                  title="Refresh notifications"
                  disabled={loading || !guardianId}
                >
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>
          <div className={styles.panelBody}>
            <p>
              This page lists the properties you wish to be notified about when
              a viewing is scheduled:
            </p>
            <ul>
              <li>
                Property Details: Includes the property name and postcode.
              </li>
              <li>
                Status: Shows whether you are "Waiting" or there is a "New
                Viewing Date," or the "Property Not Available."
              </li>
              <li>
                Cancel Button: Lets you cancel your request to be informed.
              </li>
            </ul>
            <p>
              Use this page to stay updated and book viewings for properties of
              interest.
            </p>
          </div>
        </div>
      </div>

      {/* Notifications Section - Following Laravel implementation exactly */}
      <div className={styles.notificationsSection}>
        <div className={styles.panel}>
          <div className={styles.panelHeading}>
            <h3>Keep me informed notifications</h3>
          </div>
          <div className={styles.panelBody}>
            {!guardianId ? (
              <div className={styles.noNotifications}>
                Loading guardian data...
              </div>
            ) : notifications.length > 0 ? (
              <div className={styles.dataTableWrapper}>
                <DataTable
                  columns={columns}
                  data={notifications}
                  pagination={pagination}
                  onPageChange={(page) =>
                    setPagination((p) => ({ ...p, currentPage: page }))
                  }
                  showSearch={guardianId ? true : false}
                  onSearch={(val) => setSearchValue(val)}
                  showEntries={guardianId ? true : false}
                  entriesOptions={[10, 25, 30, 50, 75, 100]}
                  defaultEntries={30}
                />
              </div>
            ) : loading ? (
              <div className={styles.loading}>
                <div className={styles.loadingSpinner}></div>
                Loading notifications...
              </div>
            ) : (
              <div className={styles.noNotifications}>
                You don't have any keep me informed notifications!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeepMeInformed;
